import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { AnimatePresence, motion } from 'motion/react'
import * as React from 'react'
import { cn } from '@/lib/utils'
import { Spinner } from './spinner'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0`,
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-11 px-4 py-2',
        icon: 'h-11 w-11 shrink-0',
        lg: 'h-11 rounded-md px-8',
        sm: 'h-9 rounded-md px-3',
        inline: 'h-auto p-0 text-base',
      },
      variant: {
        default:
          'border border-transparent bg-button-primary text-button-primary-foreground hover:bg-button-primary-hover active:bg-button-primary-active disabled:bg-neutral-150 dark:disabled:bg-neutral-850',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        ghost:
          'hover:bg-neutral-100 active:bg-neutral-200 hover:text-accent-foreground dark:hover:bg-neutral-800 dark:active:bg-neutral-750',
        link: 'text-primary font-semibold underline-offset-4 hover:underline',
        outline:
          'border border-border-subtle-1 bg-layer-1 hover:border-border-strong-1 hover:bg-neutral-100 active:bg-neutral-200 dark:hover:bg-neutral-800 dark:active:bg-neutral-750 disabled:bg-neutral-150 dark:disabled:bg-neutral-850',
        secondary:
          'bg-button-secondary text-button-secondary-foreground hover:bg-button-secondary-hover active:bg-button-secondary-active',
        neutral:
          'bg-black dark:bg-white text-white dark:text-black hover:bg-neutral-700 active:bg-neutral-650 dark:hover:bg-neutral-200 dark:active:bg-neutral-250',
        background:
          'bg-neutral-100 hover:bg-neutral-150 active:bg-neutral-200 dark:bg-neutral-850 dark:hover:bg-neutral-800 dark:active:bg-neutral-750',
      },
    },
  },
)

export type ButtonProps = {
  asChild?: boolean
  isLoading?: boolean
  tooltip?: React.ReactNode
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, size, variant, isLoading, tooltip, ...props }, ref) => {
    const buttonEl = asChild ? (
      <Slot className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
    ) : (
      <button
        className={cn(buttonVariants({ className, size, variant }))}
        ref={ref}
        {...props}
        onClick={e => {
          if (isLoading) return
          props.onClick?.(e)
        }}
      >
        <AnimatePresence>
          {isLoading && (
            <motion.div
              className="overflow-hidden"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <Spinner />
            </motion.div>
          )}
        </AnimatePresence>
        {/* Wrapped so browser translation (which swaps text nodes for <font>) can't break React's insertBefore anchor when the spinner mounts. */}
        <span className="contents">{props.children}</span>
      </button>
    )

    if (tooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{buttonEl}</TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    return buttonEl
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
